/** Plain-language labels for worker import issues and warnings returned by the server. */
export type IssueInfo = { label: string; help: string; tone: "review" | "info" | "skip" };

export const ISSUES: Record<string, IssueInfo> = {
  CODE_EXISTS: {
    label: "Code already in HKP",
    help: "Link it if it is the same person. Otherwise create them with a new code.",
    tone: "review",
  },
  NO_PREFIX: {
    label: "No prefix",
    help: "Pick a category. The sheet number is kept, e.g. _395 becomes K_395.",
    tone: "review",
  },
  UNKNOWN_PREFIX: {
    label: "Unknown prefix",
    help: "Choose a category, or mark as not a worker (e.g. a vendor).",
    tone: "review",
  },
  BLANK_NAME: {
    label: "No name",
    help: "Find out who this is. Keep it here with a note until then.",
    tone: "review",
  },
  INVALID_CODE: { label: "No code number", help: "Enter a code, or skip the row.", tone: "review" },
  DUPLICATE_CODE_IN_FILE: {
    label: "Code repeated in file",
    help: "The first row with this code was used. Decide what this one is.",
    tone: "review",
  },
  NOT_A_PERSON: { label: "Not a person", help: "Alone / NR are helper options, not workers.", tone: "skip" },
  ALREADY_PRESENT: { label: "Already added", help: "Same code and name are already in HKP.", tone: "skip" },
  EMPTY_ROW: { label: "Empty row", help: "Nothing to import.", tone: "skip" },
  SAME_NAME_OTHER_CODE: {
    label: "Same name, other code",
    help: "Treated as different people, told apart by code.",
    tone: "info",
  },
  SAME_NUMBER_OTHER_PREFIX: {
    label: "Same number, other prefix",
    help: "Both codes were kept. Worth checking later.",
    tone: "info",
  },
  CODE_NORMALIZED: { label: "Code made uppercase", help: "e.g. k_380 became K_380.", tone: "info" },
  PHONE_IGNORED: { label: "Phone not used", help: "Not a 10-digit mobile number; add it on the worker later.", tone: "info" },
  PAY_TYPE_IGNORED: { label: "Pay type not used", help: "Use PIECE, TIME or BOTH. The category's default was used.", tone: "info" },
  RATE_IGNORED: { label: "Rate not used", help: "Day or hour rate wasn't a number like 400 or 52.50.", tone: "info" },
  NO_CATEGORY: {
    label: "Work not known",
    help: "No Work column and nothing in the name says what they do. Pick their work.",
    tone: "review",
  },
  UNKNOWN_WORK: {
    label: "Work word not known",
    help: "HKP didn't recognise the word in Work. Pick their work, or add that work type.",
    tone: "review",
  },
  NAME_IN_HKP: {
    label: "Name already in HKP",
    help: "Link it if it is the same person, or add them as someone new.",
    tone: "review",
  },
  DUPLICATE_NAME_IN_FILE: {
    label: "Same name twice in the file",
    help: "The first row was used. Is this a different person with the same name?",
    tone: "review",
  },
  CODE_WILL_BE_GIVEN: { label: "Code given by HKP", help: "No code in the file; the next code of their work is given.", tone: "info" },
  CATEGORY_FROM_NAME: { label: "Work taken from the name", help: "e.g. “Raju Karigar” was added as a weaver.", tone: "info" },
};

export const issueInfo = (code: string): IssueInfo =>
  ISSUES[code] || { label: code.replace(/_/g, " ").toLowerCase(), help: "", tone: "info" };

export const REVIEW_ORDER = [
  "NAME_IN_HKP",
  "DUPLICATE_NAME_IN_FILE",
  "UNKNOWN_WORK",
  "NO_CATEGORY",
  "CODE_EXISTS",
  "NO_PREFIX",
  "UNKNOWN_PREFIX",
  "BLANK_NAME",
  "DUPLICATE_CODE_IN_FILE",
  "INVALID_CODE",
];
